import React, { useMemo } from "react";
import { Box } from "@mui/material";
import useTheme from "../../../../common/hooks/useTheme";
import { Theme } from "../../../../providers/ThemeProvider";
import { useConsultation } from "../../ConsultationContext";
import {
  ServicesPage,
  SchedulingPage,
  SchedulingService,
  ServiceContent,
} from "../../../new-reports/types/newReportTypes";
import { isServiceConnectedToSystem } from "../../../platform/components/score/RecommendedServicesTab";
import ColoredDot from "../shared/ColoredDot";
import SectionHeader from "../shared/SectionHeader";

/**
 * Section 3 · Sub 3.1 — Bio-Optimization Services grid. Visual reference:
 * Figma node 14687:138537 (header + 3-column grid of service cards).
 *
 * Each card (Figma node 14647:101087):
 *   ┌─────────────────────────────────────┐
 *   │ [136×69 image]  category            │
 *   │                 service name        │
 *   ├─────────────────────────────────────┤
 *   │ [W1: 2x] [W2: 3x] [W3: 2x] …        │
 *   ├─────────────────────────────────────┤
 *   │ Mitochondria · Metabolism           │
 *   └─────────────────────────────────────┘
 *
 * Real data sources:
 *   - `prescriptionPlan` SchedulingPage → cadence (W1: 2x …) + service category
 *   - `prescriptionPlan` ServicesPage    → imageURL (joined by serviceName) +
 *                                          `rational.list[0].conditions[]` rules
 *                                          consumed by `isServiceConnectedToSystem`
 *
 * Derived (not mocked):
 *   - per-service `impactsSystems` tag list — computed by running
 *     `isServiceConnectedToSystem(service, system)` (the same predicate the
 *     score screen and Section 2 use) against every system in `data.systems`
 *     and collecting the matching `system.name`s. Single source of truth for
 *     service-to-system relationships across all three surfaces.
 */
const SERVICE_TYPE_LABEL: Record<number, string> = {
  1: "Longevity treatment",
  2: "IV therapy",
  3: "Supplements",
  4: "Pharmaceuticals",
  5: "Pharmaceutical IV",
  6: "Lab work",
  7: "Medical procedure",
};

const formatCadence = (svc: SchedulingService): string[] => {
  // `scheduleValues` is positional — index 0 = W1/Q1, index 1 = W2/Q2, etc.
  // Map FIRST and filter empty slots AFTER, so the surviving chips keep
  // their correct slot numbers when a schedule has gaps (e.g. ["2","-","3","-"]
  // renders as "W1: 2 · W3: 3", not "W1: 2 · W2: 3"). Quarterly schedules
  // use the same logic for parity (Q1..Q4) instead of the generic "Quarterly:".
  const prefix = svc.schedulingType === 1 ? "W" : "Q";
  return (svc.scheduleValues ?? [])
    .map((v, i) => (v && v !== "-" ? `${prefix}${i + 1}: ${v}` : null))
    .filter(Boolean) as string[];
};

type Card = {
  id: string;
  serviceName: string;
  category: string;
  serviceTypeCode: number;
  cadence: string[];
  imageURL?: string;
  impactsSystems: string[];
};

const ServiceCard: React.FC<{ card: Card }> = ({ card }) => {
  const theme: Theme = useTheme();
  return (
    <Box sx={styles.card(theme)}>
      <Box sx={styles.top}>
        <Box sx={styles.imageWrap(theme)}>
          {card.imageURL ? (
            <Box
              component="img"
              src={card.imageURL}
              alt={card.serviceName}
              sx={styles.imageImg}
            />
          ) : (
            <Box sx={styles.imagePlaceholder(theme)}>★</Box>
          )}
        </Box>
        <Box sx={styles.content}>
          <Box sx={styles.category(theme)}>{card.category}</Box>
          <Box sx={styles.name(theme)}>{card.serviceName}</Box>
        </Box>
      </Box>

      {card.cadence.length > 0 && (
        <Box sx={styles.chipsRow(theme)}>
          {card.cadence.map((part, i) => (
            <Box key={i} component="span" sx={styles.chip(theme)}>
              {part}
            </Box>
          ))}
        </Box>
      )}

      {card.impactsSystems.length > 0 && (
        <Box sx={styles.footer(theme)}>
          {card.impactsSystems.map((s, i) => (
            <React.Fragment key={i}>
              {i > 0 && (
                <ColoredDot color={theme.colors_ds.NEUTRAL_TEXT_WEAK} size={3} />
              )}
              <Box component="span">{s}</Box>
            </React.Fragment>
          ))}
        </Box>
      )}
    </Box>
  );
};

const BioOptimizationGrid: React.FC = () => {
  const theme: Theme = useTheme();
  const { data } = useConsultation();

  const cards = useMemo<Card[]>(() => {
    const plan = data.prescriptionPlan ?? [];
    const scheduling = plan.find(
      (p) => p.pageType === "scheduling"
    ) as SchedulingPage | undefined;
    const services = plan.find(
      (p) => p.pageType === "services"
    ) as ServicesPage | undefined;

    // Build a lookup so we can attach imageURLs + rational rules from the
    // ServicesPage to the scheduled items by serviceName.
    //
    // The services array can contain DUPLICATE entries for the same
    // serviceName — one carrying rational rules + an image, another with
    // empty `rational.list[0].conditions` (e.g. backend duplicates a row
    // when a service is offered under multiple categories). A naive
    // last-write-wins dict picks the empty-rules row and silently strips
    // the impacts footer. Prefer the entry that actually has rules; on a
    // tie, prefer the one with an imageURL.
    const serviceByName: Record<string, ServiceContent> = {};
    const conditionsOf = (s: ServiceContent | undefined) =>
      (s?.rational?.list?.[0]?.conditions ?? []).length;
    services?.services?.forEach((s) => {
      if (!s?.serviceName) return;
      const key = s.serviceName.toLowerCase();
      const existing = serviceByName[key];
      if (
        !existing ||
        conditionsOf(s) > conditionsOf(existing) ||
        (conditionsOf(s) === conditionsOf(existing) &&
          !!s.imageURL && !existing.imageURL)
      ) {
        serviceByName[key] = s;
      }
    });

    // Derive impactsSystems by running the SAME predicate the score screen
    // and Section 2 use — a service impacts a system iff any of its rational
    // conditions reference a biomarker id that's part of that system. Without
    // this, the bio-opt grid would diverge from the other two surfaces (and
    // historically did — Lung had 5 services on the score screen / Section 2
    // but 0 here because the old hand-curated mock only tagged one service).
    const systems = data.systems ?? [];
    const computeImpacts = (svc: ServiceContent | undefined): string[] => {
      if (!svc) return [];
      const matched: string[] = [];
      for (const sys of systems) {
        if (isServiceConnectedToSystem(svc, sys) && sys.name) {
          matched.push(sys.name);
        }
      }
      return matched;
    };

    const fromScheduling: Card[] =
      scheduling?.schedule?.map((sv, i) => {
        const matched = serviceByName[sv.serviceName?.toLowerCase()];
        return {
          id: `sched-${i}`,
          serviceName: sv.serviceName,
          category: SERVICE_TYPE_LABEL[sv.serviceTypeCode] ?? "Service",
          serviceTypeCode: sv.serviceTypeCode,
          cadence: formatCadence(sv),
          imageURL: matched?.imageURL,
          impactsSystems: computeImpacts(matched),
        };
      }) ?? [];

    return fromScheduling;
  }, [data.prescriptionPlan, data.systems]);

  return (
    <Box component="section" sx={styles.section}>
      <SectionHeader
        title="Bio-Optimization Services"
        subtitle="Personalized services and their target systems."
      />
      {cards.length === 0 ? (
        <Box sx={styles.subtitle(theme)}>
          No prescribed services for this client yet.
        </Box>
      ) : (
        <Box sx={styles.grid}>
          {cards.map((card) => (
            <ServiceCard key={card.id} card={card} />
          ))}
        </Box>
      )}
    </Box>
  );
};

export default BioOptimizationGrid;

const styles = {
  section: {
    width: "100%",
    display: "flex",
    flexDirection: "column" as const,
    gap: "16px",
  },
  // Section-empty fallback text reuses subtitle typography. Kept local
  // because it's only referenced from one place; `SectionHeader` handles
  // the real header block.
  subtitle: (theme: Theme) => ({
    fontFamily: "var(--font-Regular)",
    fontSize: "12px",
    lineHeight: 1.6,
    color: theme.colors_ds.NEUTRAL_TEXT_WEAK,
  }),
  grid: {
    display: "grid",
    gridTemplateColumns: "repeat(3, 1fr)",
    gap: "16px",
  },
  card: (theme: Theme) => ({
    backgroundColor: theme.colors_ds.NEUTRAL_BG_MAIN,
    border: `1px solid ${theme.colors_ds.NEUTRAL_BORDER_WEAK}`,
    borderRadius: "8px",
    overflow: "hidden",
    display: "flex",
    flexDirection: "column" as const,
    minWidth: 0,
  }),
  top: {
    display: "flex",
    alignItems: "stretch",
    width: "100%",
    minWidth: 0,
  },
  imageWrap: (theme: Theme) => ({
    width: "136px",
    height: "69px",
    flexShrink: 0,
    backgroundColor: theme.colorTokens.N[30],
    overflow: "hidden",
  }),
  imageImg: {
    width: "100%",
    height: "100%",
    display: "block",
    objectFit: "cover" as const,
    objectPosition: "center" as const,
  },
  imagePlaceholder: (theme: Theme) => ({
    width: "100%",
    height: "100%",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    color: theme.colorTokens.N[60],
    fontSize: "22px",
  }),
  content: {
    flex: 1,
    minWidth: 0,
    display: "flex",
    flexDirection: "column" as const,
    gap: "8px",
    padding: "12px",
  },
  category: (theme: Theme) => ({
    fontFamily: "var(--font-Regular)",
    fontSize: "12px",
    lineHeight: "15px",
    letterSpacing: "-0.13px",
    color: theme.colors_ds.NEUTRAL_TEXT_WEAK,
  }),
  name: (theme: Theme) => ({
    fontFamily: "var(--font-Medium)",
    fontSize: "14px",
    lineHeight: 1.6,
    color: theme.colors_ds.NEUTRAL_TEXT_DEFAULT,
  }),
  chipsRow: (theme: Theme) => ({
    borderTop: `1px solid ${theme.colors_ds.NEUTRAL_BORDER_WEAK}`,
    padding: "12px",
    display: "flex",
    flexWrap: "wrap" as const,
    gap: "8px",
  }),
  chip: (theme: Theme) => ({
    backgroundColor: theme.colorTokens.N[30],
    borderRadius: "8px",
    padding: "8px",
    fontFamily: "var(--font-Medium)",
    fontSize: "14px",
    lineHeight: "12px",
    letterSpacing: "-0.13px",
    color: theme.colors_ds.NEUTRAL_TEXT_WEAK,
    whiteSpace: "nowrap" as const,
  }),
  footer: (theme: Theme) => ({
    borderTop: `1px solid ${theme.colors_ds.NEUTRAL_BORDER_WEAK}`,
    padding: "12px",
    display: "flex",
    flexWrap: "wrap" as const,
    alignItems: "center",
    gap: "4px",
    fontFamily: "var(--font-Medium)",
    fontSize: "12px",
    lineHeight: "12px",
    letterSpacing: "-0.13px",
    color: theme.colors_ds.NEUTRAL_TEXT_WEAK,
  }),
};
